// @flow
import React, { useEffect } from 'react';
import { withTranslation, Link, nextRoutes } from '@i18n';
import PackageMiniflow from '@components/PackageMiniflow/index';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { waitForPageReady } from '@lib';
import { NextSeo } from 'next-seo';
import { isUserAgentMobile, humanize } from '@lib/helpers';

import {
  LocationIcon,
  HotelIcon,
  HospitalIcon,
  CityTourIcon,
  DoctorsCardIcon,
  HospitalCardIcon,
  ViewProfile,
  BedIcon
} from '@images/svg/index';
import { PROFILE_PACKAGE_FORM, YAPITA_HEALTH_URL } from '@routes';
import Testimonial from '@screens/Blog/Article/components/TestimonialList/testimonialCard';
import { StyledCoverMaxWidthWrapper } from '@screens/HowItWorks/components/TeaserSideJob/styles';
import type { GlobalState } from '@globalTypes';
import FAQQUESTIONEXPAND from '@components/FAQQuesExpand/index';
import { useBreakpoint } from '@hooks';
import { colors } from '@components/ListItemFacility/styles';
import BulletList from '@components/BulletList/index';
import GridCarousel from '@components/GridCarousel/index';
import { requestBlogTestimonial, resetBlogArticles } from '@screens/Blog/redux/actions';
import { requestPackageDetails } from './redux/actions';

import {
  StyledMainCover,
  StyledTopSectionWrapper,
  StyledTopImageGridWapper,
  StyledTopTextGridWapper,
  StyledTitleMain,
  StyledTitleSecond,
  StyledLocationWrapper,
  StyledLocationIconWrapper,
  StyledLocationText,
  StyledFacilityCards,
  StyledFacilityCardsTextWrapper,
  StyledFacilityCardsTitle,
  StyledFacilityCardsSubtitle,
  StyledPriceWrapper,
  StyledSaveWrapper,
  StyledAmountContent,
  StyledOldPrice,
  StyledPayNow,
  StyledBookButton,
  StyledBookText,
  StyledGridWrapper,
  StyledGridUnit,
  StyledMainGridWrapper,
  StyledFormWrapper,
  StyledDoctorsCardWrapper,
  StyledDoctorsCardTitle,
  StyledCardsLeftWrapper,
  StyledCardsRightWrapper,
  StyledCardImage,
  StyledDoctorsNameTitle,
  StyledDoctorsNameSubtitle,
  StyledDoctorsHospitalName,
  StyledDoctorsHeadings,
  StyledSepratorGray,
  StyledViewProfileButton,
  StyledViewProfileButtonWrapper,
  StyledHospitalCardWrapper,
  StyledHospitalCardTitle,
  StyledHospitalCardDescription,
  StyledComponentWrappers,
  StyledHeadlines,
  StyledPriceHeadline,
  StyledCardItemsWrapper,
  StyledSaveInfoContent,
  StyledFacilityCardsWrapperDesktopVisible,
  StyledMobileVisible,
  StyledDesktopVisible,
  StyledTestimonialWrapper
} from './styles';

import CostComparision from './components/CostComparision/index';
import AccomodationCard from './components/AccomodationCard/index';
import CityTourCard from './components/CityTourCard/index';
import AdditionalBenefits from './components/AdditionalBenefits/index';
import TreatmentInfoCard from './components/TreatmentInfoCard/index';
import PackageDetailsComponent from './components/PackageDetailsComponent/index';
import type { Props, State } from './types';

import ImagePreview from './components/ImagePreview/index';

const PackageDetails = ({
  t,
  packageDetails,
  isModal,
  requestBlogTestimonial,
  resetBlogArticles,
  blogData
}: Props) => {
  const packageData: State = packageDetails;
  const relationshipData = packageDetails
  && packageDetails.data
  && packageDetails.data.relationships;
  const { locale } = nextRoutes;
  const calcSize = (length, onScreenCount) => Math.ceil(length / onScreenCount) * onScreenCount;
  const isMobile = useBreakpoint('mobile', 'desktop');
  useEffect(() => {
    requestBlogTestimonial(8, relationshipData?.treatment?.data?.id);

    return () => resetBlogArticles();
  }, []);

  const hospitalDescriptionArray: Array<string> = t('HospitalCard.Description', {
    country: relationshipData?.facility?.data?.attributes?.address_country,
    city: relationshipData?.facility?.data?.attributes?.address_city,
    noOfBeds: relationshipData?.facility?.data?.attributes?.number_of_beds
  }).split(',');

  const experience = (): string => {
    const dob = new Date(relationshipData?.doctor?.data?.attributes?.practicing_from || '');
    const month_diff = Date.now() - dob.getTime();
    const age_dt: Date = new Date(month_diff);
    const year = age_dt.getUTCFullYear();
    return Math.abs(year - 1970).toString();
  };
  const tenPercentPrice = () => {
    const finalPrice = packageData?.data?.attributes?.selling_price || 0;
    return (finalPrice / 100) * (packageData?.data?.attributes?.booking_percentage || 10);
  };

  const doctorDepartment = relationshipData?.doctor?.data?.attributes?.department || '';

  const metaData: {
    MetaTitle: string,
    MetaDescription: string
  } = t('Seo:PackageSeo', {
    returnObjects: true,
    name: packageData?.data?.attributes?.name
  });

  const DoctorsCard = () => (
    <StyledDoctorsCardWrapper>
      <StyledDoctorsCardTitle>
        <DoctorsCardIcon />
        {t('DoctorCard.DoctorHeading')}
      </StyledDoctorsCardTitle>
      <StyledGridWrapper>
        <StyledGridUnit size={{ mobile: 1 / 1, desktop: 2 / 5 }}>
          <StyledCardsLeftWrapper>
            <StyledCardImage>
              <img
                src={relationshipData?.doctor?.data?.attributes?.image_url}
                alt={relationshipData?.doctor?.data?.attributes?.name}
              />
            </StyledCardImage>
            <StyledDoctorsNameTitle>
              {relationshipData?.doctor?.data?.attributes?.name}
            </StyledDoctorsNameTitle>
            <StyledDoctorsHospitalName>
              <HospitalCardIcon />
              {relationshipData?.facility?.data?.attributes?.name}
            </StyledDoctorsHospitalName>
          </StyledCardsLeftWrapper>
        </StyledGridUnit>
        <StyledGridUnit size={{ mobile: 1 / 1, desktop: 3 / 5 }}>
          <StyledCardsRightWrapper>
            <StyledDoctorsHeadings>{t('DoctorCard.Specialization')}</StyledDoctorsHeadings>
            <StyledDoctorsNameSubtitle>
              {relationshipData?.doctor?.data?.attributes?.speciality}
            </StyledDoctorsNameSubtitle>
            <StyledSepratorGray />
            <StyledDoctorsHeadings>{t('DoctorCard.Department')}</StyledDoctorsHeadings>
            <StyledDoctorsNameSubtitle>
              {humanize(doctorDepartment.split('_'))}
            </StyledDoctorsNameSubtitle>
            <StyledSepratorGray />
            <StyledDoctorsHeadings>{t('DoctorCard.Experience')}</StyledDoctorsHeadings>
            <StyledDoctorsNameSubtitle>
              {`${experience()}${t('DetailsJob:YearExperience')}`}
            </StyledDoctorsNameSubtitle>
          </StyledCardsRightWrapper>
        </StyledGridUnit>
      </StyledGridWrapper>
      <StyledViewProfileButtonWrapper>
        <a
          href={`/en/doctors/${relationshipData?.doctor?.data?.attributes?.slug || ''}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <StyledViewProfileButton>
            {t('ViewProfileButton')}
            <ViewProfile />
          </StyledViewProfileButton>
        </a>
      </StyledViewProfileButtonWrapper>
    </StyledDoctorsCardWrapper>
  );
  const HospitalCard = () => (
    <StyledHospitalCardWrapper>
      <StyledDoctorsCardTitle>
        <HospitalCardIcon />
        {relationshipData?.facility?.data?.attributes?.name}
      </StyledDoctorsCardTitle>
      <StyledGridWrapper>
        <StyledGridUnit size={{ mobile: 1 / 1, desktop: 2 / 5 }}>
          <StyledCardsLeftWrapper>
            <StyledCardImage>
              <img
                src={relationshipData?.facility?.data?.attributes?.banner_photo_url}
                alt={relationshipData?.facility?.data?.attributes?.name}
              />
            </StyledCardImage>
            <StyledDoctorsNameTitle>
              {relationshipData?.facility?.data?.attributes?.name}
            </StyledDoctorsNameTitle>
          </StyledCardsLeftWrapper>
        </StyledGridUnit>
        <StyledGridUnit size={{ mobile: 1 / 1, desktop: 3 / 5 }}>
          <StyledCardsRightWrapper>
            <StyledHospitalCardTitle>
              <BedIcon />

              {t('HospitalCard.Beds', {
                noOfBeds: relationshipData?.facility?.data?.attributes?.number_of_beds
              })}
            </StyledHospitalCardTitle>
            <StyledHospitalCardDescription>
              <BulletList bullets={hospitalDescriptionArray} />
            </StyledHospitalCardDescription>
          </StyledCardsRightWrapper>
        </StyledGridUnit>
      </StyledGridWrapper>
      <StyledViewProfileButtonWrapper>
        <a
          href={`/en/facility/${relationshipData?.facility?.data?.attributes?.slug || ''}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <StyledViewProfileButton>
            {t('ViewProfileButton')}
            <ViewProfile />
          </StyledViewProfileButton>
        </a>
      </StyledViewProfileButtonWrapper>
    </StyledHospitalCardWrapper>
  );

  return (
    <>
      <NextSeo
        title={packageData?.data?.attributes?.meta_title || metaData.MetaTitle}
        description={packageData?.data?.attributes?.meta_description || metaData.MetaDescription}
        canonical={`${YAPITA_HEALTH_URL}/${locale}/packages/${packageData?.data?.attributes?.slug
          || ''}`}
        openGraph={{
          url: `${YAPITA_HEALTH_URL}/${locale}/packages/${packageData?.data?.attributes?.slug
            || ''}`,
          title: packageData?.data?.attributes?.meta_title || metaData.MetaTitle,
          description: packageData?.data?.attributes?.meta_description || metaData.MetaDescription,
          images: [
            {
              url:
                packageData?.data?.attributes?.seo_image.url
                || 'https://yapita-production.s3.ap-south-1.amazonaws.com/images/yapita-health-og.jpg',
              width: 800,
              height: 600,
              alt: 'Yapita Health - Your Medical Partner',
              type: 'image/jpg'
            }
          ],
          siteName: 'Yapita Health'
        }}
        twitter={{
          handle: '@yapitahealth',
          site: '@yapitahealth',
          cardType: 'summary_large_image'
        }}
      />

      <StyledCoverMaxWidthWrapper>
        <StyledMainCover>
          <StyledTopSectionWrapper>
            <StyledMobileVisible>
              <StyledTitleMain>{packageData?.data?.attributes?.name}</StyledTitleMain>
              <StyledLocationWrapper>
                <StyledLocationIconWrapper>
                  <LocationIcon />
                </StyledLocationIconWrapper>
                <StyledLocationText>
                  {packageData?.data?.attributes?.city}
                  ,
                  {packageData?.data?.attributes?.country}
                </StyledLocationText>
              </StyledLocationWrapper>
            </StyledMobileVisible>
            <StyledTopImageGridWapper size={{ mobile: 1 / 1, desktop: 1 / 2 }}>
              <>
                {packageData?.data?.attributes?.cover_image_url
                && relationshipData?.treatment_images.data ? (
                  <ImagePreview packageData={packageData} relationshipData={relationshipData} />
                ) : (
                  <img src="/static/images/svg/ProfilePackage/staticPackage.png" alt="" />
                )}
              </>
            </StyledTopImageGridWapper>
            <StyledTopTextGridWapper size={{ mobile: 1 / 1, desktop: 1 / 2 }}>
              <StyledDesktopVisible>
                <StyledTitleSecond>{packageData?.data?.attributes?.name}</StyledTitleSecond>
                <StyledLocationWrapper>
                  <StyledLocationIconWrapper>
                    <LocationIcon />
                  </StyledLocationIconWrapper>
                  <StyledLocationText>
                    {packageData?.data?.attributes?.city}
                    ,
                    {packageData?.data?.attributes?.country}
                  </StyledLocationText>
                </StyledLocationWrapper>
              </StyledDesktopVisible>

              <StyledFacilityCardsWrapperDesktopVisible>
                <StyledFacilityCards size={{ mobile: 1 / 3 }}>
                  <StyledCardItemsWrapper>
                    <HospitalIcon />
                    <StyledFacilityCardsTextWrapper>
                      <StyledFacilityCardsTitle>{t('DaysInHospital')}</StyledFacilityCardsTitle>
                      <StyledFacilityCardsSubtitle>
                        {packageData?.data?.attributes?.days_in_hospital}
                        {t('Days')}
                      </StyledFacilityCardsSubtitle>
                    </StyledFacilityCardsTextWrapper>
                  </StyledCardItemsWrapper>
                </StyledFacilityCards>

                <StyledFacilityCards size={{ mobile: 1 / 3 }}>
                  <StyledCardItemsWrapper>
                    <HotelIcon />
                    <StyledFacilityCardsTextWrapper>
                      <StyledFacilityCardsTitle>{t('DaysInHotel')}</StyledFacilityCardsTitle>
                      <StyledFacilityCardsSubtitle>
                        {packageData?.data?.attributes?.days_in_hotel}
                        {t('Days')}
                      </StyledFacilityCardsSubtitle>
                    </StyledFacilityCardsTextWrapper>
                  </StyledCardItemsWrapper>
                </StyledFacilityCards>

                <StyledFacilityCards size={{ mobile: 1 / 3 }}>
                  <StyledCardItemsWrapper>
                    <img src="/svg/packageScreen/cash.svg" alt="cash-card" />
                    <StyledFacilityCardsTextWrapper>
                      <StyledFacilityCardsTitle>{t('CashVoucher')}</StyledFacilityCardsTitle>
                      <StyledFacilityCardsSubtitle>
                        {t('MoneyData', { money: 75 })}
                      </StyledFacilityCardsSubtitle>
                    </StyledFacilityCardsTextWrapper>
                  </StyledCardItemsWrapper>
                </StyledFacilityCards>

                <StyledFacilityCards size={{ mobile: 1 / 3 }}>
                  <StyledCardItemsWrapper>
                    <CityTourIcon />
                    <StyledFacilityCardsTextWrapper>
                      <StyledFacilityCardsTitle>{t('CityTourTitle')}</StyledFacilityCardsTitle>
                      <StyledFacilityCardsSubtitle>
                        {t('PersonData', { total: 2 })}
                      </StyledFacilityCardsSubtitle>
                    </StyledFacilityCardsTextWrapper>
                  </StyledCardItemsWrapper>
                </StyledFacilityCards>

                <StyledFacilityCards size={{ mobile: 1 / 3 }}>
                  <StyledCardItemsWrapper>
                    <img src="/svg/packageScreen/refund.svg" alt="refund" />
                    <StyledFacilityCardsTextWrapper>
                      <StyledFacilityCardsTitle>{t('Refund')}</StyledFacilityCardsTitle>
                      <StyledFacilityCardsSubtitle>{t('Cancellation')}</StyledFacilityCardsSubtitle>
                    </StyledFacilityCardsTextWrapper>
                  </StyledCardItemsWrapper>
                </StyledFacilityCards>

                <StyledFacilityCards size={{ mobile: 1 / 3 }}>
                  <StyledCardItemsWrapper>
                    <img src="/svg/packageScreen/room-upgrade.svg" alt="room-upgrade" />
                    <StyledFacilityCardsTextWrapper>
                      <StyledFacilityCardsTitle>{t('RoomUpgrade')}</StyledFacilityCardsTitle>
                      <StyledFacilityCardsSubtitle color={colors.saveGreen}>
                        {t('Free')}
                      </StyledFacilityCardsSubtitle>
                    </StyledFacilityCardsTextWrapper>
                  </StyledCardItemsWrapper>
                </StyledFacilityCards>
              </StyledFacilityCardsWrapperDesktopVisible>

              <StyledPriceWrapper>
                <StyledPriceHeadline>{t('CostTitle')}</StyledPriceHeadline>

                <StyledSaveWrapper>
                  <StyledAmountContent>
                    <StyledOldPrice>
                      $
                      {packageData?.data?.attributes?.original_price}
                    </StyledOldPrice>
                    <StyledPayNow>
                      $
                      {packageData?.data?.attributes?.selling_price}
                    </StyledPayNow>
                  </StyledAmountContent>
                  <StyledSaveInfoContent>
                    {t('SaveInfo', { amount: packageData?.data?.attributes?.saving_amount })}
                  </StyledSaveInfoContent>
                </StyledSaveWrapper>
              </StyledPriceWrapper>
              <Link
                href={PROFILE_PACKAGE_FORM}
                params={{ slug: packageData?.data?.attributes?.slug }}
              >
                <StyledBookButton>
                  {t('BookNow', {
                    amount:
                      ((packageData?.data?.attributes?.selling_price ?? 0)
                        * (packageData?.data?.attributes?.booking_percentage ?? 10))
                      / 100
                  })}
                </StyledBookButton>
              </Link>

              <StyledBookText>{t('PayLater')}</StyledBookText>
            </StyledTopTextGridWapper>
          </StyledTopSectionWrapper>
          <StyledMainGridWrapper>
            <StyledGridUnit size={{ mobile: 1 / 1, desktop: 3 / 5 }}>
              <StyledComponentWrappers>
                <StyledHeadlines>
                  {t('CountryComparisionTitle', { treatment: packageData?.data?.attributes?.name })}
                </StyledHeadlines>
                <CostComparision packageData={packageData} relationshipData={relationshipData} />
              </StyledComponentWrappers>
              <StyledComponentWrappers>
                <DoctorsCard />
              </StyledComponentWrappers>
              <StyledComponentWrappers>
                <HospitalCard />
              </StyledComponentWrappers>
              <StyledComponentWrappers>
                <StyledHeadlines>{t('AccomodationTitle')}</StyledHeadlines>
                <AccomodationCard packageData={packageData} relationshipData={relationshipData} />
              </StyledComponentWrappers>
              {relationshipData?.city_tour?.data && (
                <StyledComponentWrappers>
                  <StyledHeadlines>{t('CityTourTitle')}</StyledHeadlines>
                  <CityTourCard data={relationshipData?.city_tour?.data?.attributes} />
                </StyledComponentWrappers>
              )}
              <StyledComponentWrappers>
                <StyledHeadlines>{t('AdditionalBenefits')}</StyledHeadlines>
                <AdditionalBenefits packageData={packageData} relationshipData={relationshipData} />
              </StyledComponentWrappers>

              <StyledComponentWrappers>
                <PackageDetailsComponent data={relationshipData?.treatment_infos?.data} />
              </StyledComponentWrappers>
              <StyledComponentWrappers>
                <TreatmentInfoCard data={packageData?.data?.attributes} />
              </StyledComponentWrappers>
            </StyledGridUnit>
            <StyledGridUnit size={{ mobile: 1 / 1, desktop: 2 / 5 }}>
              <StyledFormWrapper>
                <PackageMiniflow
                  amount={tenPercentPrice()}
                  packageData={packageData}
                  isModal={isModal}
                />
              </StyledFormWrapper>
            </StyledGridUnit>
          </StyledMainGridWrapper>

          {(blogData.testimonialData || []).length > 0 && (
            <>
              <StyledHeadlines>{t('PatientTestimonials')}</StyledHeadlines>
              <StyledTestimonialWrapper>
                <GridCarousel
                  onScreenCount={isMobile ? 1 : 3}
                  length={(blogData.testimonialData || []).length}
                  paddingTop={isMobile ? 2 : 4}
                >
                  {(blogData.testimonialData || []).map(item => (
                    <Testimonial
                      testimonial={item}
                      customSize={{
                        mobile:
                          1 / calcSize((blogData.testimonialData || []).length, isMobile ? 1 : 3)
                      }}
                    />
                  ))}
                </GridCarousel>
              </StyledTestimonialWrapper>
            </>
          )}

          {relationshipData && relationshipData?.treatment_faqs?.data.length > 0 && (
            <StyledComponentWrappers style={{ padding: '8px' }}>
              <FAQQUESTIONEXPAND
                data={relationshipData?.treatment_faqs?.data}
                heading={t('FAQHeading')}
              />
            </StyledComponentWrappers>
          )}
        </StyledMainCover>
      </StyledCoverMaxWidthWrapper>
    </>
  );
};

PackageDetails.getInitialProps = async ({ req, query, store }) => {
  store.dispatch(requestPackageDetails(query.slug));

  await waitForPageReady(store);
  const isMobile = isUserAgentMobile(req);

  return {
    namespacesRequired: ['ProfilePackages'],
    query,
    isMobile
  };
};

const mapStateToProps = ({ packageDetails, blogData }: GlobalState): {} => ({
  packageDetails,
  blogData
});

export default compose(
  withTranslation('ProfilePackages'),
  connect(
    mapStateToProps,
    { requestPackageDetails, requestBlogTestimonial, resetBlogArticles }
  )
)(PackageDetails);
